import { useEffect, useState } from "preact/hooks";
import { useRoute } from "preact-iso";
import {
  ClipboardDocumentIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { api } from "../lib/api-client";
import { adminHref } from "../lib/platform-admin";
import { formatTimestamp } from "../lib/format";
import { useAsyncData } from "../lib/use-async-data";
import { useClipboardCopy } from "../lib/use-clipboard-copy";
import { useConfirm } from "../lib/use-confirm";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Input,
  ListItem,
  PageHeader,
  SectionHeading,
  SkeletonList,
} from "../components/ui";

export function ApiKeysPage() {
  const { params } = useRoute();
  const projectId = params.projectId!;

  const { data, error, reload, setError } = useAsyncData(
    () =>
      Promise.all([
        api.getProject(projectId),
        api.listKeys(projectId),
      ]).then(([p, list]) => ({ project: p, keys: list.items })),
    [projectId],
  );
  const project = data?.project ?? null;
  const keys = data?.keys ?? null;
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  // Scope the freshly-minted plaintext key to the project it belongs to. The
  // route component is reused across `:projectId` changes, so an id-less
  // `revealed` would leak project A's key onto project B's screen (same
  // `/keys` route, different param). See the render guard below.
  const [revealed, setRevealed] = useState<{
    projectId: string;
    id: string;
    key: string;
  } | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { copied, copy } = useClipboardCopy();
  const REVEALED_COPY_KEY = "revealed";

  // Clear per-project transient state when navigating to another project's
  // keys page without unmounting (same route, new param).
  useEffect(() => {
    setRevealed(null);
    setName("");
    setCreating(false);
  }, [projectId]);

  // Hide the revealed key after 30s so a stale tab doesn't leak the plaintext
  // to a passer-by / screen recording long after the operator has copied it.
  useEffect(() => {
    if (!revealed) return;
    const id = window.setTimeout(() => setRevealed(null), 30_000);
    return () => window.clearTimeout(id);
  }, [revealed]);

  async function onCreate(e: Event) {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const created = await api.createKey(projectId, { name });
      setRevealed({ projectId, id: created.id, key: created.key });
      setName("");
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(keyId: string) {
    const ok = await confirm({
      title: "このAPIキーを失効させますか？",
      description:
        "失効後は同じキーで認証できなくなります。新しいキーを発行して切り替えてください。",
      confirmLabel: "失効する",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.revokeKey(projectId, keyId);
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function onCopy() {
    if (!revealed) return;
    try {
      await copy(REVEALED_COPY_KEY, revealed.key);
    } catch {
      // ignore — surface nothing if clipboard API is unavailable
    }
  }

  return (
    <section class="space-y-10">
      {confirmDialog}
      <PageHeader
        backHref={adminHref(`/projects/${projectId}`)}
        backLabel={project?.name ?? "プロジェクト"}
        title="APIキー"
      />

      {revealed && revealed.projectId === projectId && (
        <Alert
          tone="warning"
          title="このキーは今だけ表示されます。コピーして保存してください。"
          onClose={() => setRevealed(null)}
        >
          <div class="flex flex-wrap items-stretch gap-2">
            <code class="min-w-0 flex-1 break-all rounded-md border border-warning-border bg-bg px-3 py-2 font-mono text-sm text-fg">
              {revealed.key}
            </code>
            <Button
              size="md"
              variant="secondary"
              iconLeft={<ClipboardDocumentIcon class="h-3.5 w-3.5" />}
              onClick={onCopy}
            >
              {copied === REVEALED_COPY_KEY ? "コピー済み" : "コピー"}
            </Button>
          </div>
        </Alert>
      )}

      <div class="space-y-4">
        <SectionHeading level={3} title="新規発行" />
        <Card>
          <CardBody>
            <form onSubmit={onCreate} class="flex flex-wrap items-end gap-3">
              <Field label="name" class="min-w-[220px] flex-1">
                <Input
                  value={name}
                  onInput={(e) =>
                    setName((e.target as HTMLInputElement).value)
                  }
                  required
                  placeholder="production"
                />
              </Field>
              <Button
                type="submit"
                variant="primary"
                iconLeft={<PlusIcon class="h-4 w-4" />}
                loading={creating}
                disabled={creating}
              >
                {creating ? "発行中…" : "発行"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {keys === null ? (
        <SkeletonList count={2} />
      ) : keys.length === 0 ? (
        <EmptyState
          icon={<KeyIcon class="h-5 w-5" />}
          title="キーはまだありません"
          description="上のフォームから「name」を入力して発行してください。"
        />
      ) : (
        <ul class="grid gap-2">
          {keys.map((k) => (
            <ListItem
              key={k.id}
              class="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
            >
              <div class="flex min-w-0 items-center gap-3">
                <span class="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-bg-subtle text-fg-subtle">
                  <KeyIcon class="h-4 w-4" />
                </span>
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-sm font-medium text-fg">
                      {k.name}
                    </span>
                    {k.revokedAt && <Badge tone="danger">revoked</Badge>}
                  </div>
                  <div class="mt-0.5 font-mono text-xs text-fg-subtle">
                    作成 {formatTimestamp(k.createdAt)} ・ 失効{" "}
                    {k.revokedAt ? formatTimestamp(k.revokedAt) : "—"}
                  </div>
                </div>
              </div>
              {!k.revokedAt && (
                <Button
                  size="sm"
                  variant="danger"
                  iconLeft={<TrashIcon class="h-3.5 w-3.5" />}
                  onClick={() => onRevoke(k.id)}
                >
                  失効
                </Button>
              )}
            </ListItem>
          ))}
        </ul>
      )}
    </section>
  );
}
